import { useState } from 'react';
import {
  ARCHIVE_REASON_MAX,
  DELETION_REASON_MAX,
  DELETION_REFUSAL,
  type BulkOutcome,
  type Stage,
} from '@/domain';
import {
  useArchiveCases,
  useAssignCases,
  useDeleteCases,
  useUnarchiveCases,
} from '@/application/usecases/useBulk';
import { useSession } from '@/application/usecases/useSession';
import type { BulkResult } from '@/application/ports/JobOrderRepository';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { useOfferUndo } from '@/features/shell/useOfferUndo';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { BulkBar } from '@/ui/data/BulkBar';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { BulkAssignDialog, type BulkAssignChoice } from './BulkAssignDialog';
import { BulkReasonDialog } from './BulkReasonDialog';
import { BulkResultDialog } from './BulkResultDialog';
import type { SelectionAction } from './permittedActions';

export interface SelectionActionsProps {
  count: number;
  /** The cases the selection covers, for archiving, taking out and deleting. */
  jobOrderNos: string[];
  /** What to assign: the trays ticked, or every tray of the cases ticked. */
  assignTarget: { kitIds: string[] } | { jobOrderNos: string[] };
  /** The one department every ticked case is at, or null when they are at several. */
  commonStage: Stage | null;
  /** Already narrowed to what this reader may do, by `usePermittedActions`. */
  actions: readonly SelectionAction[];
  onClear: () => void;
  /** Ticks the rest of the page, while some of it is not ticked. */
  onSelectPage?: () => void;
}

type Open = 'assign' | 'archive' | 'delete' | null;

/**
 * The bar over a table while cases are ticked, its dialogs, and the answer.
 *
 * A selection that goes through whole says so in a toast and clears. One that
 * does not says which cases were not done and why, grouped by what the server
 * said, and clears as well: the numbers are on the screen, and a selection
 * half acted on is not one anybody meant to act on again as it stands.
 *
 * Since the request of 5 October 2026 (third), section 2, the three dialogs
 * are their own confirmations, each with a question saying how many cases, and
 * taking a selection out of the archive, the one action here that was a single
 * tap, asks the desk roles first.
 */
export function SelectionActions({
  count,
  jobOrderNos,
  assignTarget,
  commonStage,
  actions,
  onClear,
  onSelectPage,
}: SelectionActionsProps) {
  const { data: session } = useSession();
  const outright = session?.permissions.includes('approveDeletion') === true;

  const archive = useArchiveCases();
  const unarchive = useUnarchiveCases();
  const remove = useDeleteCases();
  const assign = useAssignCases();
  const offerUndo = useOfferUndo();
  const ask = useConfirmAction();

  const [open, setOpen] = useState<Open>(null);
  const [error, setError] = useState<string | null>(null);
  const [answer, setAnswer] = useState<{ verb: string; outcomes: BulkOutcome[] } | null>(null);

  const cases = `${count} ${count === 1 ? strings.bulk.one : strings.bulk.many}`;

  function finish(verb: string, result: BulkResult) {
    setOpen(null);
    setError(null);
    onClear();
    if (result.refused > 0) {
      setAnswer({ verb, outcomes: result.results });
      return;
    }
    toast(`${result.done} ${result.done === 1 ? strings.bulk.one : strings.bulk.many} ${verb}`);
  }

  const failed = (failure: Error) => setError(failureWords(failure));

  function close() {
    setOpen(null);
    setError(null);
  }

  return (
    <>
      {count > 0 && (
        <BulkBar count={count} onClear={onClear} onSelectPage={onSelectPage}>
          {actions.includes('assign') && (
            <Button onClick={() => setOpen('assign')}>{strings.bulk.assign}</Button>
          )}
          {actions.includes('archive') && (
            <Button onClick={() => setOpen('archive')}>{strings.bulk.archive}</Button>
          )}
          {actions.includes('unarchive') && (
            <Button
              disabled={unarchive.isPending}
              onClick={() => {
                const only = jobOrderNos.length === 1 ? jobOrderNos[0] : undefined;
                ask(
                  only === undefined
                    ? strings.confirm.unarchiveMany(count)
                    : strings.confirm.unarchive(only),
                  () =>
                    unarchive
                      .mutateAsync({ jobOrderNos })
                      .then((result) => finish(strings.bulk.unarchived, result)),
                  { confirmLabel: strings.bulk.unarchive },
                );
              }}
            >
              {unarchive.isPending ? strings.bulk.sending : strings.bulk.unarchive}
            </Button>
          )}
          {actions.includes('delete') && (
            <Button onClick={() => setOpen('delete')}>{strings.bulk.delete}</Button>
          )}
        </BulkBar>
      )}

      {open === 'assign' && (
        <BulkAssignDialog
          count={count}
          commonStage={commonStage}
          busy={assign.isPending}
          error={error}
          onCancel={close}
          onConfirm={(choice: BulkAssignChoice) =>
            assign.mutate(
              {
                ...assignTarget,
                ...(choice.stage === null ? {} : { stage: choice.stage }),
                ...(choice.userId === null ? {} : { userId: choice.userId }),
              },
              {
                onSuccess: (result) => {
                  /*
                   * Whatever was sent or given can be taken back from the toast,
                   * as one: the server answers with one undo for the selection.
                   * Section 6 of the request of 5 October 2026. With refusals the
                   * list of them is shown instead, as it always was.
                   */
                  if (result.refused > 0 || result.undo == null) {
                    finish(strings.bulk.assigned, result);
                    return;
                  }
                  setOpen(null);
                  setError(null);
                  onClear();
                  offerUndo(
                    `${result.done} ${result.done === 1 ? strings.bulk.one : strings.bulk.many} ${strings.bulk.assigned}`,
                    result.undo,
                  );
                },
                onError: failed,
              },
            )
          }
        />
      )}

      {open === 'archive' && (
        <BulkReasonDialog
          title={`${strings.bulk.archiveTitle} ${cases}`}
          statement={strings.confirm.archiveMany(count)}
          lead={strings.bulk.archiveLead}
          reasonLabel={strings.bulk.archiveReasonLabel}
          placeholder={strings.bulk.archiveReasonPlaceholder}
          maxLength={ARCHIVE_REASON_MAX}
          required={false}
          confirmLabel={`${strings.bulk.archive} ${cases}`}
          busy={archive.isPending}
          error={error}
          onCancel={close}
          onConfirm={(reason) =>
            archive.mutate(
              { jobOrderNos, ...(reason === '' ? {} : { reason }) },
              { onSuccess: (result) => finish(strings.bulk.archived, result), onError: failed },
            )
          }
        />
      )}

      {open === 'delete' && (
        <BulkReasonDialog
          title={`${strings.bulk.deleteTitle} ${cases}`}
          statement={
            outright ? strings.confirm.deleteMany(count) : strings.confirm.askDeleteMany(count)
          }
          lead={outright ? strings.deletion.leadDelete : strings.deletion.leadRequest}
          reasonLabel={strings.bulk.deleteReasonLabel}
          placeholder={strings.deletion.reasonPlaceholder}
          maxLength={DELETION_REASON_MAX}
          required
          requiredMessage={DELETION_REFUSAL.reasonRequired}
          confirmLabel={outright ? `${strings.bulk.delete} ${cases}` : strings.bulk.askToDelete}
          busy={remove.isPending}
          error={error}
          onCancel={close}
          onConfirm={(reason) =>
            remove.mutate(
              { jobOrderNos, reason },
              {
                onSuccess: (result) =>
                  finish(outright ? strings.bulk.deleted : strings.bulk.deletionAsked, result),
                onError: failed,
              },
            )
          }
        />
      )}

      {answer !== null && (
        <BulkResultDialog verb={answer.verb} outcomes={answer.outcomes} onClose={() => setAnswer(null)} />
      )}
    </>
  );
}
